import {FC, MouseEvent, useState} from 'react';
import {LocalOfferOutlined} from '@mui/icons-material';
import {Box, Button, Popover, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {AppRouteEnum, AppRouteSpace, isErrorObject} from '../../../utils/helper';
import {TariffDTO} from '../../../types';
import {tariffsApi} from '../../../api';
import {useAppDispatch} from '../../../app/hooks';
import {TariffStatusButton} from '../TariffStatusButton';
import {AppDispatch, store} from '../../../app/store';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {useGetUserLanguage} from '../../../hooks/useGetUserLanguage';
import {LanguageEnum} from '../../../enums';
import {TableCard} from '../../tables/respnsiveTable/TableCard';
import {MenuButton} from '../../tables/respnsiveTable/MenuButton';
import {tariffTableCardSx} from './TariffTableCard.sx';
import {NotifyError} from 'src/utils/notification';
import {useCan} from 'src/permissions/hooks';

type DeleteTariffMutationTrigger = ReturnType<typeof tariffsApi.useDeleteTariffMutation>[0];

const handleDeleteTariff =
    (
        trigger: DeleteTariffMutationTrigger,
        dispatch: AppDispatch,
        businessId: number,
        tariffId: number
    ) =>
        async () => {
            try {
                dispatch(preloaderChangeStatus(true));
                await trigger({businessId, tariffId}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

export const TariffTableCard: FC<TariffDTO> = (tariff) => {
    const {
        id,
        name,
        nameFrench,
        nameDeutsch,
        business,
        contractsSize,
        monthlyFee,
        country,
        region,
    } = tariff;
    const intl = useIntl();
    // The same permission the desktop row checks; the phone menu was offering it to anyone.
    const canDelete = useCan('tariffs.delete');
    const [deleteTrigger] = tariffsApi.useDeleteTariffMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const [currentLanguage] = useGetUserLanguage();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const nameMap: Record<string, string> = {
        [LanguageEnum.ENGLISH]: name,
        [LanguageEnum.FRENCH]: nameFrench,
        [LanguageEnum.DEUTSCH]: nameDeutsch,
    };

    // One node, drawn by whichever of the two the width calls for: the handler and the gate around
    // it are the same in both.
    const deleteAction = canDelete ? (
        <Button
            sx={tariffTableCardSx.menuBtn}
            onClick={handleDeleteTariff(deleteTrigger, dispatch, business.id, id)}
        >
            <Typography sx={tariffTableCardSx.menuBtnText}>
                {intl.formatMessage({id: 'delete'})}
            </Typography>
        </Button>
    ) : null;

    // The menu and the overlay it opens travel together into the head's action slot: the popover
    // anchors on the button's element, not on where it sits in the card.
    const menuAction = (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<LocalOfferOutlined/>}
                    title={nameMap[currentLanguage]}
                    subtitle={business?.name}
                >
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={tariffTableCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {deleteAction}
                </Popover>
            )}
        </>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<LocalOfferOutlined/>}
                title={
                    <Box
                        component={Link}
                        to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.tariffs, String(id)])}
                        sx={tariffTableCardSx.titleLink}
                    >
                        {nameMap[currentLanguage]}
                    </Box>
                }
                subtitle={business?.name}
                action={menuAction}
            />
            <Box sx={phonePillsRowSx}>
                <TariffStatusButton tariff={tariff}/>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'pricePerMonthColumnHeader'}),
                        value: monthlyFee ? monthlyFee / 100 : 0,
                    },
                    {
                        label: intl.formatMessage({id: 'activeClientsColumnHeader'}),
                        value: contractsSize ?? '-',
                    },
                    {
                        label: intl.formatMessage({id: 'countryColumnHeader'}),
                        value: country ?? '-',
                    },
                    {
                        label: intl.formatMessage({id: 'regionColumnHeader'}),
                        value: region ?? '-',
                    },
                ]}
            />
        </TableCard>
    );
};
